'use client';

import React, { memo, useCallback } from 'react';
import { IBanner } from '@/_types';
import { AspectRatio, Box } from '@chakra-ui/react';
import Image from 'next/image';
import { Link } from '@/i18n/navigation';
import { useCustomLocale } from '@/_hooks/useCustomLocale';
import Mobile from './responsive/mobile';
import Desktop from './responsive/desktop';

const isExternalUrl = (url: string | undefined): boolean => {
  if (!url || typeof window === 'undefined') return false;
  return !url.includes(window.location.hostname);
};

const getFullUrl = (
  url: string | undefined,
  baseUrl: string | undefined,
): string => {
  if (!url || !baseUrl) return '';
  return url.includes(baseUrl) ? url : `${baseUrl}${url}`;
};

const Banner = ({ list }: { list: IBanner[] }) => {
  const currentLocale = useCustomLocale();

  const createSlides = useCallback(
    (keyPrefix: string) =>
      list
        ?.filter((item) => item?.image?.[currentLocale])
        .map((item, index) => {
          const imageUrl = getFullUrl(
            item?.image?.[currentLocale],
            process.env.BASE_URL,
          );

          const videoUrl =
            item?.media_type === 'video'
              ? getFullUrl(
                  item?.image?.[currentLocale],
                  process.env.BASE_URL_VIDEO,
                )
              : null;

          const gifUrl =
            item?.media_type === 'gif'
              ? getFullUrl(
                  item?.image?.[currentLocale],
                  process.env.BASE_URL_GIF,
                )
              : null;

          const bannerContent = (
            <AspectRatio
              ratio={3 / 1}
              overflow='hidden'
              placeItems='center'
              position='relative'
              borderRadius={{ base: '12px', lg: '20px' }}
              height={{ base: '97px', lg: '131px' }}
              maxH={'131px'}
            >
              {item?.media_type === 'video' && videoUrl ? (
                <video
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                  autoPlay
                  muted
                  loop
                  preload='metadata'
                  playsInline
                >
                  <source src={videoUrl} />
                </video>
              ) : item?.media_type === 'gif' && gifUrl ? (
                <Image
                  src={gifUrl}
                  alt={`Banner ${item.title?.[currentLocale] || ''}`}
                  style={{
                    objectFit: 'cover',
                  }}
                  fill
                />
              ) : imageUrl ? (
                <Image
                  src={imageUrl}
                  style={{
                    objectFit: 'cover',
                  }}
                  alt={`Banner ${item.title?.[currentLocale] || ''}`}
                  fill
                />
              ) : null}
            </AspectRatio>
          );

          return (
            <div
              className='embla__slide'
              key={`${keyPrefix}-${item.id || index}`}
            >
              {item?.url ? (
                <Link
                  href={item.url}
                  target={isExternalUrl(item.url) ? '_blank' : '_self'}
                  rel='noreferrer'
                  style={{ width: '100%' }}
                >
                  {bannerContent}
                </Link>
              ) : (
                bannerContent
              )}
            </div>
          );
        }),
    [list, currentLocale],
  );

  if (!list?.length) return null;

  return (
    <>
      <Box display={{ base: 'none', lg: 'block' }}>
        <Desktop slides={createSlides('desktop')} />
      </Box>
      <Box display={{ base: 'block', lg: 'none' }}>
        <Mobile slides={createSlides('mobile')} />
      </Box>
    </>
  );
};

export default memo(Banner);
